import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, Check, X, ChevronLeft, ChevronRight, Filter, Send, CircleAlert, MessagesSquare } from 'lucide-react';
import AdminLayout from '../../layouts/AdminLayout';
//import { waApi } from '../../services/api';
import { waLogsApi } from '../../services/api';

import { formatDateTime } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import WaEmulator from '../../components/WaEmulator';

const TYPE_LABELS = {
  order_created: 'Pesanan Dibuat',
  order_ready: 'Siap Diambil',
  status_update: 'Update Pesanan',
  payment_reminder: 'Reminder Bayar',
  dp_reminder: 'Reminder Bayar',
  piutang_reminder: 'Reminder Piutang',
  ngendon_reminder: 'Reminder Ngendon',
  custom: 'Pesan Manual',
  proof_notification: 'Notif Bukti Bayar',
  payment_confirmation: 'Konfirmasi Pembayaran',
};

export default function WaLogPage() {
  const { t } = useLanguage();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState(null);
  const filters = { page, limit: 20, message_type: typeFilter, date_from: dateFrom, date_to: dateTo };

  const { data, isLoading } = useQuery({
    queryKey: ['wa-logs', filters],
    queryFn: () => waLogsApi.list(filters).then(r => r.data),
  });
  const { data: stats } = useQuery({
    queryKey: ['wa-log-stats', typeFilter, dateFrom, dateTo],
    queryFn: () => waLogsApi.getStats({ message_type: typeFilter, date_from: dateFrom, date_to: dateTo }).then(r => r.data),
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / 20);

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Log WhatsApp', 'WhatsApp Log')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{total} {t('aktivitas WhatsApp', 'WhatsApp activities')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Filter size={15} className="text-slate-400" />
            <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
              data-testid="wa-log-type-filter"
              className="px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-emerald-500">
              <option value="">Semua Tipe</option>
              {Object.entries(TYPE_LABELS).map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
            </select>
            <input type="date" value={dateFrom} onChange={event => { setDateFrom(event.target.value); setPage(1); }} data-testid="wa-log-date-from" aria-label="Tanggal awal WA Log" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 outline-none focus:border-emerald-500" />
            <input type="date" value={dateTo} onChange={event => { setDateTo(event.target.value); setPage(1); }} data-testid="wa-log-date-to" aria-label="Tanggal akhir WA Log" className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 outline-none focus:border-emerald-500" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { label: 'Pesan Terkirim', value: stats?.total_sent ?? '—', icon: Send, color: 'bg-emerald-50 text-emerald-700' },
            { label: 'Pesan Gagal', value: stats?.failed ?? '—', icon: CircleAlert, color: 'bg-rose-50 text-rose-700' },
            { label: 'Total Pesan WA', value: stats?.total ?? '—', icon: MessagesSquare, color: 'bg-sky-50 text-sky-700' },
          ].map(({ label, value, icon: Icon, color }) => <div key={label} data-testid={`wa-log-stat-${label.toLowerCase().replaceAll(' ', '-')}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)]"><div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}><Icon size={18} /></div><div><p className="text-xs font-medium text-slate-400">{label}</p><p className="font-manrope text-xl font-bold text-slate-900">{value}</p></div></div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Log List */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-16">
                <MessageCircle size={36} className="text-slate-200 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">{t('Belum ada log WA', 'No WA logs yet')}</p>
              </div>
            ) : (
              <>
                <div className="divide-y divide-slate-50">
                  {items.map(log => (
                    <button key={log.id} onClick={() => setSelected(log)} data-testid={`wa-log-item-${log.id}`}
                      className={`w-full text-left px-5 py-4 hover:bg-slate-50/70 transition-colors flex items-start gap-3 ${selected?.id === log.id ? 'bg-emerald-50/50 border-l-2 border-[#0f6c44]' : ''}`}>
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${log.status === 'sent' ? 'bg-green-100' : log.status === 'simulated' ? 'bg-amber-100' : log.status === 'skipped' ? 'bg-slate-100' : 'bg-red-100'}`}>
                        {log.status === 'sent'
                          ? <Check size={14} className="text-green-600" />
                          : log.status === 'simulated' ? <MessageCircle size={14} className="text-amber-600" /> : log.status === 'skipped' ? <CircleAlert size={14} className="text-slate-500" /> : <X size={14} className="text-red-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-slate-800 truncate">{log.target}</span>
                          <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-md flex-shrink-0">
                            {TYPE_LABELS[log.message_type] || log.message_type}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {log.status === 'simulated' ? 'Simulasi Demo — tidak dikirim ke WhatsApp asli' : log.status === 'skipped' ? `Tidak dikirim — ${log.skip_reason || 'preferensi pelanggan'}` : log.message_preview || log.message?.slice(0, 80) || '—'}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(log.created_at)}</p>
                      </div>
                    </button>
                  ))}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-5 py-3 border-t border-slate-50">
                    <span className="text-xs text-slate-400">{t('Hal', 'Page')} {page} / {totalPages}</span>
                    <div className="flex gap-1.5">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="wa-log-previous-page-button"
                        className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40 transition-colors">
                        <ChevronLeft size={16} className="text-slate-600" />
                      </button>
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="wa-log-next-page-button"
                        className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40 transition-colors">
                        <ChevronRight size={16} className="text-slate-600" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Preview Panel */}
          <div className="hidden lg:block">
            {selected ? (
              <div className="sticky top-5 space-y-3">
                <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Preview Pesan</p>
                  <p className="text-xs text-slate-600 font-medium">{selected.target}</p>
                  <p className="text-xs text-slate-400">{formatDateTime(selected.created_at)}</p>
                </div>
                <WaEmulator
                  message={selected.message || selected.message_preview || ''}
                  senderName={selected.target}
                  timestamp={selected.created_at}
                />
              </div>
            ) : (
              <div className="sticky top-5 bg-white rounded-2xl border border-dashed border-slate-200 p-8 text-center">
                <MessageCircle size={28} className="text-slate-200 mx-auto mb-2" />
                <p className="text-xs text-slate-400">{t('Klik pesan untuk preview', 'Click a message to preview')}</p>
              </div>
            )}
          </div>

          {/* Mobile: full-screen preview modal */}
          {selected && (
            <div className="lg:hidden fixed inset-0 bg-black/60 z-50 flex items-end" onClick={() => setSelected(null)}>
              <div className="bg-white w-full rounded-t-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{selected.target}</p>
                    <p className="text-xs text-slate-400">{formatDateTime(selected.created_at)}</p>
                  </div>
                  <button onClick={() => setSelected(null)} data-testid="close-wa-log-preview-button" className="p-1.5 hover:bg-slate-100 rounded-xl">
                    <X size={18} className="text-slate-500" />
                  </button>
                </div>
                <WaEmulator
                  message={selected.message || selected.message_preview || ''}
                  senderName={selected.target}
                  timestamp={selected.created_at}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
